import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import { Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { getInstalledBaseTypeIcon } from "@dreebit-registry/vsm-microservice-components";
import AssetProductImage from "../AssetProductImage";
import { getNodeShortcutTarget } from "../../utils/getNodeShortcutTarget";
import { getPathNodesAbove } from "../../utils/installedBasePath";
import { getInstalledBaseNodeTypeName } from "../../utils/getInstalledBaseNodeTypeName";
import { getSystemTypeLabel } from "../../config/systemTypes";
import { resolveNodeDisplayImage } from "../../utils/nodeDisplayImage";

interface Props {
    node: any;
}

function buildPathString(titles: string[]): string {
    if (titles.length === 0) return "";
    if (titles.length <= 3) {
        return titles.join(" / ");
    }
    return `${titles[0]} / … / ${titles[titles.length - 2]} / ${titles[titles.length - 1]}`;
}

// One shared row shell for every node type: image/icon, a fixed-width identity
// column, then the client/path column at the same x-position in every row.
// Only ASSET rows get a trailing product name column.
const NodeShortcutItem: React.FC<Props> = ({ node }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const ownerName = _.get(node, "owner.name", "");
    const ownerIndustryType = _.get(node, "owner.industryType");
    const asset = node?.assetLocation ? node.assetLocation.asset : null;
    const system = node?.system ?? null;

    const pathTitles = getPathNodesAbove(node)
        .map((n) => n.title ?? "")
        .filter((title) => title !== "");
    const pathString = buildPathString(pathTitles);
    const fullPath = pathTitles.join(" / ");

    const primaryText = asset ? _.get(asset, "serialNumber") : node?.title;
    const secondaryText = asset
        ? _.get(asset, "product.productCode")
        : system
            ? getSystemTypeLabel(system.systemType, t)
            : getInstalledBaseNodeTypeName(node?.nodeType, t);
    const productName = asset ? _.get(asset, "product.name", "") : "";

    // User-set node image → product image / system-type PNG → placeholder. Assets
    // and systems always get an image tile (branded placeholder when none); other
    // node types show their type icon unless the user set an image.
    const { src: tileImage, source: tileImageSource } = resolveNodeDisplayImage(node);
    const showImageTile = tileImageSource !== "none" || !!asset || !!system;

    return (
        <Box
            onClick={() => navigate(getNodeShortcutTarget(node))}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 12px",
                cursor: "pointer",
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:hover": { backgroundColor: "#f5f5f5" },
            }}
        >
            {/* Image / node type icon */}
            <Box
                sx={{
                    flexShrink: 0,
                    width: 50,
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {showImageTile ? (
                    <AssetProductImage
                        src={tileImage}
                        industryType={ownerIndustryType}
                        style={{ width: 50, height: 50, borderRadius: 4 }}
                    />
                ) : (
                    getInstalledBaseTypeIcon(node?.nodeType?.key, 28)
                )}
            </Box>

            {/* Identity: serial + product code (assets) or title + type */}
            <Box sx={{ width: 140, flexShrink: 0 }}>
                <Tooltip title={primaryText ?? ""} enterDelay={700}>
                    <Typography
                        level="body-sm"
                        sx={{ fontWeight: "bold", color: "primary.500" }}
                        noWrap
                    >
                        {primaryText || "---"}
                    </Typography>
                </Tooltip>
                <Typography level="body-xs" textColor="neutral.500" noWrap>
                    {secondaryText || "---"}
                </Typography>
            </Box>

            {/* Customer + installed base path */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-sm" sx={{ fontWeight: "bold" }} noWrap>
                    {ownerName}
                </Typography>
                {pathString && (
                    <Tooltip title={fullPath} enterDelay={700}>
                        <Typography level="body-xs" textColor="neutral.500" noWrap>
                            {pathString}
                        </Typography>
                    </Tooltip>
                )}
            </Box>

            {/* Product name (assets only) */}
            {asset && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={productName} enterDelay={700}>
                        <Typography level="body-sm" textColor="neutral.600" noWrap>
                            {productName || "---"}
                        </Typography>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default NodeShortcutItem;
